import type { DisplayRef } from '@repo/sdk/models/components';
import { RefValueKind } from '@repo/sdk/models/components';

/**
 * True when a metric should use boolean-style presentation (ON/OFF), aligned with
 * equipment-details-metric-renderer (`Boolean` / `on-off` displayUnits, ref `units` boolean, or
 * `valueKind` boolean). Used by facility map pills and other call sites that need the same rules.
 */
export const isBooleanMetricDisplay = (metric: DisplayRef | undefined): boolean => {
  if (!metric?.ref) return false;
  const displayUnits = metric.displayUnits ?? '';
  if (displayUnits === 'Boolean' || displayUnits === 'on-off') return true;
  if (metric.ref.units === 'boolean') return true;
  if (metric.ref.valueKind === RefValueKind.Boolean) return true;
  return false;
};

export const parseBooleanLike = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (value == null) return undefined;

  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;

  return undefined;
};

// TODO(UX-3724): Stop defaulting boolean displays to On/Off and require boolean displayUnits from
// display_template so values can render beyond On/Off when configured. https://crossnokaye.atlassian.net/browse/UX-3724
export const getOnOffLabel = (
  value: unknown,
  labelCase: 'upper' | 'title' = 'upper'
): string | undefined => {
  const parsed = parseBooleanLike(value);
  if (parsed == null) return undefined;

  if (labelCase === 'title') return parsed ? 'On' : 'Off';
  return parsed ? 'ON' : 'OFF';
};
